import type {RowData} from '../types/RowData';

export const SELECT_EAGRID_ROWS_EVENT = 'select_eag_grid_rows_event';

export type SelectDatagridRowsTarget = {
  /** How the resolved rows combine with the current selection. Defaults to 'set' (replace). */
  mode?: 'set' | 'add' | 'remove';
} & (
  | {
      /**
       * Explicit row ids to target. With mode 'set'/'add', ids not currently loaded are
       * ignored; mode 'remove' also unselects ids whose rows are no longer loaded (a
       * selection can outlive its page). An empty array with mode 'set' clears the selection.
       */
      rowIds: string[];
      predicate?: never;
    }
  | {
      /**
       * Matches rows by their data, evaluated inside the grid against the currently loaded
       * rows. Cells are objects (`{value, style}`) behind an `unknown` index signature —
       * read them with `getRowCellValue(row, columnKey)` (exported next to this function):
       * `(row) => getRowCellValue(row, 'itemType') === 'MATERIAL'`. Only visible and
       * predefined columns are present on a row; a throwing predicate drops the request.
       *
       * WARNING: the channel is a broadcast — when several grids share this gridCode
       * (e.g. one grid per card on the same page), EVERY one of them evaluates the
       * predicate against ITS OWN rows and applies the result, so a 'set' predicate can
       * mass-select (and wipe the selection) in sibling grids. Prefer `rowIds` whenever
       * more than one grid with this gridCode can be mounted: ids from one instance's
       * rows cannot match another instance's rows.
       */
      predicate: (row: RowData) => boolean;
      rowIds?: never;
    }
);

export type SelectEagridRowsCustomEvent = {
  gridCode: string;
  target: SelectDatagridRowsTarget;
};

declare global {
  interface WindowEventMap {
    [SELECT_EAGRID_ROWS_EVENT]: CustomEvent<SelectEagridRowsCustomEvent>;
  }
}

/**
 * Selects rows in a mounted EagGrid from outside the grid (same channel pattern as
 * refreshDatagrid / applyDataGridQuery). Fire-and-forget: inert when no grid with
 * this code is mounted, and — like refreshDatagrid — a broadcast: every mounted grid
 * with this gridCode applies the request.
 *
 * Timing: while the grid is still loading (definition pending, no data fetch landed yet,
 * or a fetch in flight), the request is queued and applied once a fetch settles
 * successfully — so `refreshDatagrid(code); selectDatagridRows(code, ...)` targets the
 * refreshed rows. If the awaited fetch fails, queued requests are dropped (fire-and-forget).
 *
 * Scope and modes:
 * - Only rows on the currently loaded page can be selected (server-side pagination);
 *   mode 'remove' may additionally unselect ids that are no longer loaded.
 * - A 'set' request whose targets match nothing leaves the selection unchanged
 *   (unmatched ids are ignored); clear explicitly with `{rowIds: []}`.
 * - rowSelectMode 'NONE' ignores the request. In 'SINGLE' mode a one-row request
 *   replaces the current selection (same as a row click); multi-row requests are ignored.
 */
export const selectDatagridRows = (gridCode: string, target: SelectDatagridRowsTarget) => {
  const event = new CustomEvent(SELECT_EAGRID_ROWS_EVENT, {detail: {gridCode, target}});

  window.dispatchEvent(event);
};
